import { SelectOptionValue } from 'dashboard/components-lib/MultiSelect/MultiSelect';
import { Period, UnixTime } from 'dashboard/lib/unixTime';
import {
  FilterDimensions,
  FilteredVerificationType,
} from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/VerificationFilteredMetrics';
import { VerificationType } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

export interface VerificationStatusSummary {
  type: VerificationType;
  created: number;
  passed: number;
  processed: number;
  'processed-rate': number;
  'pass-rate': number;
  'success-average-attempts'?: number;
  'failed-average-attempts'?: number;
  'updated-at'?: Iso8601DateString;
}

// Group is the grouped value when one decides to group off of a dimension.
// For example, when someone groups off of Country Select, we’ll see that it’s populated with
// CA, US or whatever. We’ll then know we need to handle the data differently as a result
export interface VerificationVolumeIntervalMetrics {
  processed?: number;
  'pass-rate'?: number;
  group?: string;
}

export interface VerificationVolumeIntervals {
  data: Partial<Record<VerificationType, Array<VerificationVolumeDatum>>>;
  domain: [number, number];
  period: Period;
}

export interface VerificationVolumeSingleInterval {
  data: VerificationVolumeDatum[];
  domain: [number, number];
  period: Period;
}

export type DynamicFilterSet = Partial<Record<FilterDimensions, SelectOptionValue<string>[]>>;

// Handling the typing of the dynamic filters on verifications values
// We must have a FilteredVerificationType paired with the keys of dimensions that
// be used by the dropdowns in the filter cards
export type DynamicFilters = Partial<Record<FilteredVerificationType, DynamicFilterSet>>;

// Note the snake_case -- we don't re-case meta attributes in network responses
export interface VerificationVolumeDatum extends VerificationVolumeIntervalMetrics {
  created_timestamp: UnixTime;
}

export type VerificationTemplatesFilterOptions = Partial<
  Record<VerificationType, { name: string; token: string }[]>
>;
export interface FilterOptions {
  aamva_issuing_authority: string[];
  aamva_device_groups: string[];
  aamva_platforms: string[];
  database_country_codes: string[];
  database_device_groups: string[];
  database_platforms: string[];
  government_id_capture_methods: string[];
  government_id_device_groups: string[];
  government_id_issuing_countries: string[];
  government_id_issuing_subdivisions: string[];
  government_id_selected_countries: string[];
  government_id_types: string[];
  selfie_capture_methods: string[];
  selfie_device_groups: string[];
  selfie_platforms: string[];
  verification_templates: VerificationTemplatesFilterOptions;
}

export enum FilterOptionKey {
  AamvaIssuingAuthorities = 'aamvaIssuingAuthorities',
  AamvaDeviceGroups = 'aamvaDeviceGroups',
  AamvaPlatforms = 'aamvaPlatforms',
  DatabaseCountryCodes = 'databaseCountryCodes',
  DatabaseDeviceGroups = 'databaseDeviceGroups',
  DatabasePlatforms = 'databasePlatforms',
  GovernmentIdCaptureMethods = 'governmentIdCaptureMethods',
  GovernmentIdDeviceGroups = 'governmentIdDeviceGroups',
  GovernmentIdIssuingCountries = 'governmentIdIssuingCountries',
  GovernmentIdIssuingSubdivisions = 'governmentIdIssuingSubdivisions',
  GovernmentIdSelectedCountries = 'governmentIdSelectedCountries',
  GovernmentIdTypes = 'governmentIdTypes',
  SelfieCaptureMethods = 'selfieCaptureMethods',
  SelfieDeviceGroups = 'selfieDeviceGroups',
  SelfiePlatforms = 'selfiePlatforms',
  VerificationTemplates = 'verificationTemplates',
}

export interface VerificationMetricsFilteredVolumeResponse
  extends VerificationMetricsFilteredVolumeSummary {
  filter_options: FilterOptions;
}

export interface VerificationMetricsFilteredVolumeSummary {
  data: Array<VerificationStatusSummary>;
}

// Note the snake_case -- we don't re-case meta attributes in network responses
export enum MetricsGroupBy {
  Inquiry = 'inquiries',
}
